import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import { RadioButton } from 'primereact/radiobutton';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import dayjs from 'dayjs';
import { Consumption, Disposal, Material, Receipt } from '../types';
import { consumptionAPI, disposalsAPI, materialsAPI, receiptsAPI } from '../services/api';
import { escapeHtml, openPrintWindow } from '../utils/printableReport';

type ReportType = 'all' | 'consumption' | 'receipts' | 'disposals';

const formatDateSr = (d: Date) => d.toLocaleDateString('sr-RS');

const LogisticsReports: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [materials, setMaterials] = useState<Material[]>([]);
  const [consumptions, setConsumptions] = useState<Consumption[]>([]);
  const [archivedConsumptions, setArchivedConsumptions] = useState<Consumption[]>([]);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [disposals, setDisposals] = useState<Disposal[]>([]);

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [selectedMaterialIds, setSelectedMaterialIds] = useState<string[]>([]);
  const [reportType, setReportType] = useState<ReportType>('all');
  const [groupConsumptionByShift, setGroupConsumptionByShift] = useState(false);
  const [includeArchive, setIncludeArchive] = useState(false);

  const materialOptions = useMemo(
    () => materials.map((m) => ({ label: m.name, value: m._id })),
    [materials]
  );

  const selectedMaterialNames = useMemo(() => {
    const set = new Set(selectedMaterialIds);
    return materials.filter((m) => set.has(m._id)).map((m) => m.name);
  }, [materials, selectedMaterialIds]);

  const fetchBaseData = async () => {
    setLoading(true);
    try {
      const [mats, cons, archCons, rec, disp] = await Promise.all([
        materialsAPI.getAll(),
        consumptionAPI.getAll(),
        consumptionAPI.getArchived().catch(() => [] as Consumption[]),
        receiptsAPI.getAll(),
        disposalsAPI.getAll()
      ]);
      setMaterials(mats);
      setConsumptions(cons);
      setArchivedConsumptions(
        (archCons || []).map((c) => ({ ...c, isArchived: true }))
      );
      setReceipts(rec);
      setDisposals(disp);
    } catch (e) {
      console.error(e);
      setError('Greška pri dohvatanju podataka.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBaseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildFiltered = () => {
    if (!fromDate || !toDate) {
      return { cons: [] as Consumption[], rec: [] as Receipt[], disp: [] as Disposal[] };
    }
    const from = dayjs(fromDate).startOf('day');
    const to = dayjs(toDate).endOf('day');
    const materialSet = new Set(selectedMaterialIds);
    const matchMaterial = (materialId: string) => materialSet.size === 0 || materialSet.has(materialId);

    const consSource = includeArchive
      ? [...consumptions, ...archivedConsumptions]
      : consumptions;
    const cons = consSource.filter((c) => {
      const d = dayjs(c.date, 'YYYY-MM-DD');
      return d.valueOf() >= from.valueOf() && d.valueOf() <= to.valueOf() && matchMaterial(c.materialId);
    });
    const rec = receipts.filter((r) => {
      const d = dayjs(r.receiptDate, 'YYYY-MM-DD');
      return d.valueOf() >= from.valueOf() && d.valueOf() <= to.valueOf() && matchMaterial(r.materialId);
    });
    const disp = disposals.filter((dsp) => {
      const d = dayjs(dsp.disposalDate, 'YYYY-MM-DD');
      return d.valueOf() >= from.valueOf() && d.valueOf() <= to.valueOf() && matchMaterial(dsp.materialId);
    });
    return { cons, rec, disp };
  };

  const handleGenerate = () => {
    setError('');
    if (!fromDate || !toDate) {
      setError('Početni i završni datum su obavezni.');
      return;
    }
    if (dayjs(toDate).isBefore(dayjs(fromDate), 'day')) {
      setError('Završni datum ne može biti pre početnog.');
      return;
    }

    const { cons, rec, disp } = buildFiltered();
    const includeCons = reportType === 'all' || reportType === 'consumption';
    const includeRec = reportType === 'all' || reportType === 'receipts';
    const includeDisp = reportType === 'all' || reportType === 'disposals';

    const periodStr = `${formatDateSr(fromDate)} - ${formatDateSr(toDate)}`;
    const materialsStr = selectedMaterialNames.length ? selectedMaterialNames.join(', ') : 'Svi materijali';
    const archiveStr = includeArchive
      ? 'Aktivni + arhivirani zapisi (potrošnja)'
      : 'Samo aktivni zapisi';

    let body = `
      <style>
        .archived-row { background: #fff7ed; }
        .badge-archive { display: inline-block; padding: 1px 6px; margin-left: 6px;
          font-size: 10px; border: 1px solid #f59e0b; color: #b45309;
          background: #fffbeb; border-radius: 10px; }
      </style>
      <h1>Izveštaji - Logistika</h1>
      <div class="meta">
        <div><strong>Period:</strong> ${escapeHtml(periodStr)}</div>
        <div><strong>Materijali:</strong> ${escapeHtml(materialsStr)}</div>
        <div><strong>Obuhvat:</strong> ${escapeHtml(archiveStr)}</div>
        <div class="muted">Generisano: ${escapeHtml(new Date().toLocaleString('sr-RS'))}</div>
      </div>
    `;

    if (includeCons) {
      const totalKg = cons.reduce((s, c) => s + (c.totalConsumption || 0), 0);
      const archivedKg = cons
        .filter((c) => c.isArchived)
        .reduce((s, c) => s + (c.totalConsumption || 0), 0);
      const archiveLine = includeArchive
        ? `<div><strong>Od toga arhivirano:</strong> ${escapeHtml(archivedKg.toFixed(2))} kg</div>`
        : '';
      body += `<h2>Potrošnja</h2><div class="meta"><div><strong>Ukupno:</strong> ${escapeHtml(totalKg.toFixed(2))} kg</div>${archiveLine}</div>`;

      if (groupConsumptionByShift) {
        const groupKey = (c: Consumption) => `${c.date}__${c.shift}`;
        const groups = new Map<string, Consumption[]>();
        cons.forEach((c) => {
          const k = groupKey(c);
          groups.set(k, [...(groups.get(k) || []), c]);
        });
        const keys = Array.from(groups.keys()).sort((a, b) => b.localeCompare(a));
        keys.forEach((k) => {
          const [date, shift] = k.split('__');
          const rows = groups.get(k) || [];
          const shiftLabel = shift === 'first' ? 'I smena' : shift === 'second' ? 'II smena' : 'III smena';
          const subtotal = rows.reduce((s, c) => s + (c.totalConsumption || 0), 0);
          body += `<div class="meta"><div><strong>${escapeHtml(date)}</strong> — ${escapeHtml(shiftLabel)} (ukupno ${escapeHtml(subtotal.toFixed(2))} kg)</div></div>`;
          body += `<table>
            <thead>
              <tr>
                <th>Materijal</th>
                <th>Šarža</th>
                <th>Zaposleni</th>
                <th class="right">Broj korišćenja</th>
                <th class="right">Ukupno (kg)</th>
              </tr>
            </thead>
            <tbody>
              ${rows
                .map((c) => {
                  const cls = c.isArchived ? ' class="archived-row"' : '';
                  const badge = c.isArchived ? '<span class="badge-archive">arhiva</span>' : '';
                  return `<tr${cls}>
                      <td>${escapeHtml(c.materialName)}${badge}</td>
                      <td>${escapeHtml(c.resinName)}</td>
                      <td>${escapeHtml(c.employeeName)}</td>
                      <td class="right">${escapeHtml(c.usageCount)}</td>
                      <td class="right">${escapeHtml((c.totalConsumption || 0).toFixed(2))}</td>
                    </tr>`;
                })
                .join('')}
            </tbody>
          </table>`;
        });
      } else {
        body += `<table>
          <thead>
            <tr>
              <th>Datum</th>
              <th>Smena</th>
              <th>Zaposleni</th>
              <th>Šarža</th>
              <th>Materijal</th>
              <th class="right">Broj korišćenja</th>
              <th class="right">Ukupno (kg)</th>
            </tr>
          </thead>
          <tbody>
            ${cons
              .slice()
              .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
              .map((c) => {
                const shiftLabel = c.shift === 'first' ? 'I smena' : c.shift === 'second' ? 'II smena' : 'III smena';
                const cls = c.isArchived ? ' class="archived-row"' : '';
                const badge = c.isArchived ? '<span class="badge-archive">arhiva</span>' : '';
                return `<tr${cls}>
                  <td>${escapeHtml(c.date)}</td>
                  <td>${escapeHtml(shiftLabel)}</td>
                  <td>${escapeHtml(c.employeeName)}</td>
                  <td>${escapeHtml(c.resinName)}</td>
                  <td>${escapeHtml(c.materialName)}${badge}</td>
                  <td class="right">${escapeHtml(c.usageCount)}</td>
                  <td class="right">${escapeHtml((c.totalConsumption || 0).toFixed(2))}</td>
                </tr>`;
              })
              .join('')}
          </tbody>
        </table>`;
      }
    }

    if (includeRec) {
      const totalQty = rec.reduce((s, r) => s + (r.quantity || 0), 0);
      body += `<h2>Prijemnice</h2><div class="meta"><div><strong>Ukupno:</strong> ${escapeHtml(totalQty.toFixed(2))}</div></div>`;
      body += `<table>
        <thead>
          <tr>
            <th>Datum</th>
            <th>Vreme</th>
            <th>Materijal</th>
            <th>Transporter</th>
            <th class="right">Količina</th>
            <th>Jedinica</th>
            <th>Napomena</th>
          </tr>
        </thead>
        <tbody>
          ${rec
            .slice()
            .sort((a, b) => (a.receiptDate < b.receiptDate ? 1 : a.receiptDate > b.receiptDate ? -1 : 0))
            .map(
              (r) => `<tr>
                <td>${escapeHtml(r.receiptDate)}</td>
                <td>${escapeHtml(r.receiptTime)}</td>
                <td>${escapeHtml(r.materialName)}</td>
                <td>${escapeHtml(r.transporter)}</td>
                <td class="right">${escapeHtml((r.quantity || 0).toFixed(2))}</td>
                <td>${escapeHtml(r.unit)}</td>
                <td>${escapeHtml(r.notes || '')}</td>
              </tr>`
            )
            .join('')}
        </tbody>
      </table>`;
    }

    if (includeDisp) {
      const totalQty = disp.reduce((s, d) => s + (d.quantity || 0), 0);
      body += `<h2>Rashodovanja</h2><div class="meta"><div><strong>Ukupno:</strong> ${escapeHtml(totalQty.toFixed(2))}</div></div>`;
      body += `<table>
        <thead>
          <tr>
            <th>Datum</th>
            <th>Vreme</th>
            <th>Materijal</th>
            <th>Razlog</th>
            <th class="right">Količina</th>
            <th>Jedinica</th>
            <th>Lokacija</th>
            <th>Opis</th>
          </tr>
        </thead>
        <tbody>
          ${disp
            .slice()
            .sort((a, b) => (a.disposalDate < b.disposalDate ? 1 : a.disposalDate > b.disposalDate ? -1 : 0))
            .map(
              (d) => `<tr>
                <td>${escapeHtml(d.disposalDate)}</td>
                <td>${escapeHtml(d.disposalTime)}</td>
                <td>${escapeHtml(d.materialName)}</td>
                <td>${escapeHtml(d.reason)}</td>
                <td class="right">${escapeHtml((d.quantity || 0).toFixed(2))}</td>
                <td>${escapeHtml(d.unit)}</td>
                <td>${escapeHtml(d.location || '')}</td>
                <td>${escapeHtml(d.description || '')}</td>
              </tr>`
            )
            .join('')}
        </tbody>
      </table>`;
    }

    const title = `Izvestaj-Logistika-${dayjs(fromDate).format('YYYYMMDD')}-${dayjs(toDate).format('YYYYMMDD')}`;
    const ok = openPrintWindow(title, body);
    if (!ok) {
      setError('Browser je blokirao popup. Dozvolite popups za ovaj sajt pa pokušajte ponovo.');
    }
  };

  return (
    <div className="p-4">
      <h2 className="m-0 mb-4">Izveštaji - Logistika</h2>

      {error && <Message severity="error" text={error} className="mb-3" />}

      <Card>
        <div className="grid">
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="block mb-2">Početni datum</label>
              <Calendar
                value={fromDate}
                onChange={(e) => setFromDate(e.value as Date)}
                placeholder="Izaberite početni datum"
                dateFormat="dd.mm.yy"
                showIcon
                appendTo="self"
                className="w-full"
              />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="block mb-2">Završni datum</label>
              <Calendar
                value={toDate}
                onChange={(e) => setToDate(e.value as Date)}
                placeholder="Izaberite završni datum"
                dateFormat="dd.mm.yy"
                showIcon
                appendTo="self"
                className="w-full"
              />
            </div>
          </div>

          <div className="col-12">
            <div className="field">
              <label className="block mb-2">Materijali</label>
              <MultiSelect
                value={selectedMaterialIds}
                options={materialOptions}
                onChange={(e) => setSelectedMaterialIds(e.value || [])}
                placeholder="Izaberite materijale (prazno = svi)"
                display="chip"
                filter
                appendTo="self"
                // Ensure the overlay (appendTo="self") is positioned relative to this component,
                // otherwise it can appear at the top of the page depending on layout/CSS.
                style={{ width: '100%', position: 'relative' }}
              />
              <small className="text-600">
                Možete izabrati jedan ili više materijala. Ako ne izaberete nijedan, biće prikazani svi materijali.
              </small>
            </div>
          </div>

          <div className="col-12">
            <div className="field">
              <label className="block mb-2">Tip izveštaja</label>
              <div className="flex flex-wrap gap-4">
                <div className="flex align-items-center gap-2">
                  <RadioButton inputId="rt_all" value="all" checked={reportType === 'all'} onChange={(e) => setReportType(e.value)} />
                  <label htmlFor="rt_all">Sve (Potrošnja, Prijemnice, Rashodovanja)</label>
                </div>
                <div className="flex align-items-center gap-2">
                  <RadioButton
                    inputId="rt_cons"
                    value="consumption"
                    checked={reportType === 'consumption'}
                    onChange={(e) => setReportType(e.value)}
                  />
                  <label htmlFor="rt_cons">Samo Potrošnja</label>
                </div>
                <div className="flex align-items-center gap-2">
                  <RadioButton
                    inputId="rt_rec"
                    value="receipts"
                    checked={reportType === 'receipts'}
                    onChange={(e) => setReportType(e.value)}
                  />
                  <label htmlFor="rt_rec">Samo Prijemnice</label>
                </div>
                <div className="flex align-items-center gap-2">
                  <RadioButton
                    inputId="rt_disp"
                    value="disposals"
                    checked={reportType === 'disposals'}
                    onChange={(e) => setReportType(e.value)}
                  />
                  <label htmlFor="rt_disp">Samo Rashodovanja</label>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="flex align-items-center gap-2">
              <Checkbox
                inputId="group_shift"
                checked={groupConsumptionByShift}
                onChange={(e) => setGroupConsumptionByShift(!!e.checked)}
              />
              <label htmlFor="group_shift">Grupiši potrošnju po smenama (prikazuje šarže i radnike)</label>
            </div>
            <small className="text-600">
              Kada je označeno, izveštaj će prikazati detaljne podatke o potrošnji za svaku smenu, uključujući korišćene šarže i radnike.
            </small>
          </div>

          <div className="col-12">
            <div className="flex align-items-center gap-2">
              <Checkbox
                inputId="include_archive"
                checked={includeArchive}
                onChange={(e) => setIncludeArchive(!!e.checked)}
              />
              <label htmlFor="include_archive">
                Uključi arhivirane podatke
                {includeArchive && archivedConsumptions.length > 0 && (
                  <span className="ml-2 text-600">
                    (dostupno {archivedConsumptions.length} arhiviranih zapisa)
                  </span>
                )}
              </label>
            </div>
            <small className="text-600">
              Kada je označeno, u izveštaj o potrošnji ulaze i arhivirani zapisi iz prethodnih perioda.
              Arhivirani redovi su vizuelno označeni. Prijemnice i rashodovanja se ne arhiviraju.
            </small>
          </div>

          <div className="col-12">
            <Button
              label="Generiši PDF"
              icon="pi pi-file-pdf"
              className="w-full"
              onClick={handleGenerate}
              loading={loading}
              severity="info"
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default LogisticsReports;


